/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import cx from 'classnames';
import type { VoidFunctionComponent } from 'react';

import { SNIPPET_GROUPING } from '../../../../../shared/featureFlags/flagNames';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import { Checkbox, IconAndNode, icons } from '../../../generic';
import PackageLabelsList from '../../../shared/packageLabels/PackageLabelsList';
import useSnippetGrouping from '../hooks/useSnippetGrouping';
import useSnippetsForPackage from '../hooks/useSnippetsForPackage';
import type { Snippet } from '../../../../../shared/snippets';
import type { SnippetSelectionState } from '../hooks/useSnippetSelectionState';

import IssueCounts from './IssueCounts';

import styles from './index.module.less';

const snippetKindIcons = {
  snippet: icons.receipt,
  file: icons.fileOutline,
};

export type Props = {
  snippet: Snippet;
  selectionState: SnippetSelectionState,
  shouldShowBorderTop: boolean;
  shouldShowBorderBottom: boolean;
  onSelectSnippet: (snippet: Snippet) => void;
};

/**
 * Displays snippet row for the snippets table.
 */
const SnippetRow: VoidFunctionComponent<Props> = ({
  snippet,
  selectionState,
  shouldShowBorderTop,
  shouldShowBorderBottom,
  onSelectSnippet,
}) => {
  const isSnippetGroupingEnabled = useIsFeatureEnabled(SNIPPET_GROUPING);
  const { snippetGrouping } = useSnippetGrouping();
  const isGroupingByPackage = isSnippetGroupingEnabled && snippetGrouping === 'package';

  // Use the snippet that have been loaded for a given package to handle "all to some" selection state.
  const { data: packageSnippetData } = useSnippetsForPackage(snippet.packageId, isGroupingByPackage);
  const packageSnippets = packageSnippetData?.pages.flatMap((page) => page.results) ?? [];

  return (
    <div
      key={snippet.id}
      className={cx(styles.tableBodyRow, {
        [styles.tableBodyRowPackages]: isGroupingByPackage,
        [styles.tableBodyRowRejected]: !!snippet.rejectionDetails,
        [styles.tableBodyRowBorderTop]: shouldShowBorderTop,
        [styles.tableBodyRowBorderBottom]: shouldShowBorderBottom,
      })}
      onClick={() => onSelectSnippet(snippet)}
    >
      {isGroupingByPackage && (
        // Empty span to align with expand/collapse checkbox in package rows
        <span />
      )}
      <span>
        <Checkbox
          isChecked={selectionState.isSnippetChecked(snippet.id, snippet.packageId)}
          onChange={() => selectionState.toggleSnippet(snippet.id, snippet.packageId, packageSnippets)}
          shouldStopPropagation
          isRippleDisabled
        />
      </span>
      <span>
        <IconAndNode icon={snippetKindIcons[snippet.kind]} marginAmount="0.5em">
          <span className={cx({ [styles.packageRejected]: !!snippet.rejectionDetails })}>{snippet.package}</span>
        </IconAndNode>
      </span>
      <span>{snippet.version}</span>
      <span>
        <IssueCounts id={snippet.id} isRejected={!!snippet.rejectionDetails} issueCounts={snippet.issueCounts} />
      </span>
      <span>{snippet.matchCount}</span>
      <span>
        <PackageLabelsList
          labels={snippet.labels}
          dependencyVersion={snippet.version}
          labelPrefix=""
        />
      </span>
    </div>
  );
};

export default SnippetRow;
